import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  useAnalyticsColumnFeeds,
  useAnalyticsWorkflowActions,
  type AnalyticsColumnFeeds,
} from "../useAnalyticsWorkflows";
import { useAnalyticsInputFlag } from "./useAnalyticsInputFlag";
import {
  ANALYTICS_INPUTS_SIGN_IN_REQUIRED_COPY,
  useHybridTestingAvailability,
} from "../useHybridTestingAvailability";
import {
  bindingTargets,
  deletedWorkflowColumnNames,
  documentAssignmentRequest,
  feedColumns,
  feedsReadState,
  sampleRowAssignmentRequest,
  targetKey,
  targetsAssignedTo,
  targetsFilledBy,
  type BindingTarget,
  type BoundDocumentId,
  type FeedsReadState,
} from "../../lib/analyticsFeeds";
import { getColumnDisplayName } from "../../lib/columnNameUtils";
import type { Procedure } from "../../models/audit/auditSession";
import type { AnalyticsOwner } from "../../lib/analyticsOwner";

/**
 * The analytics cells' whole contract, or nothing at all.
 *
 * Absent means the procedure declares no analytics column or the feature is off,
 * and a table then renders exactly the cells it always did — not a disabled cell
 * and not an empty track. Both surfaces that show these columns are shared with
 * procedures that have no analytics at all, so the absent case is the one that
 * must not move.
 *
 * One object rather than a prop each so a caller can memoise it once for a whole
 * list: `DocumentRow` is memoised on prop identity, and a fresh callback per
 * member per render would defeat that for every row.
 */
export interface DocumentRowAnalytics {
  targets: readonly BindingTarget[];
  /**
   * What the read behind `targets` produced. Carried because an empty `targets`
   * is the shape a read in flight, a failed read and a workflow with no file
   * parameters all share.
   */
  feedsState: FeedsReadState;
  /**
   * The analytics columns whose workflow the feeds read reported deleted, by
   * the name the table shows them under. They contribute nothing to `targets`,
   * and this is what keeps that absence from reading as a workflow with no file
   * inputs.
   */
  deletedColumnNames: readonly string[];
  /**
   * True while a binding write is in flight.
   *
   * For the parameter surfaces, which hold no local state: a parameter's
   * checkmark only appears once the write's refetch lands, so without this the
   * picker stays live through the round trip and a second click writes again.
   *
   * The unmark transaction counts as one write for its whole length, clears and
   * flag write together: it decides what to clear from a snapshot taken at its
   * start, so a binding written into it is one it will neither clear nor leave
   * reachable.
   */
  pending: boolean;
  /**
   * Why every write here is held, or null when none is. Set while the session
   * did not sign in with Optro: the controls render disabled and say this, and
   * the values already written stay readable.
   */
  writesHeldReason: string | null;
  /**
   * True while this document's own flag write, or the unmark transaction around
   * it, is in flight.
   *
   * Per row rather than per session, so the row the auditor acted on is the
   * only one whose switch goes dead. The transaction is wider than its flag
   * write: it clears the document's assignments first, and a switch live over
   * those clears could be pressed again into the middle of them.
   */
  isPendingFor: (documentId: string) => boolean;
  onSetAnalyticsInput: (documentId: BoundDocumentId, next: boolean) => void;
  onAssign: (documentId: BoundDocumentId, target: BindingTarget) => void;
  /**
   * Declare that the pair is answered by the sample's own row, as a file.
   *
   * Takes no document and no sample: the binding names neither. The row is built
   * from the population when the scope is resolved, so the sample is the one
   * being resolved for. It is the only answer whose content differs per sample,
   * which is what lets a workflow know which sample it is testing.
   *
   * Written from the row whose subject *is* the generated file, so the table's
   * one control means one thing on every row: this row's subject fills that
   * input.
   */
  onUseSampleRow: (target: BindingTarget) => void;
  onClear: (target: BindingTarget) => void;
  /**
   * Clear the pairs the sample's own row still fills, out of the caller's list,
   * under one pending gesture, one sequential clear per pair, stopping at the
   * first refusal.
   *
   * The list is intersected with a re-read rather than taken as given, because
   * the clear is keyed on the pair alone: an agent rebinding one of those pairs
   * to a file makes it that file's answer, and clearing it on the strength of a
   * list read before that write deletes an assignment the auditor never saw and
   * is never told about.
   *
   * Resolves with the pairs this row may still fill. Empty is the one answer a
   * caller may record its own `off` on, and it covers two outcomes — every pair
   * cleared, and nothing left for this row to clear — because the row fills
   * nothing in both. They are told apart in what the auditor is shown, never in
   * the return. A re-read that fails resolves with the caller's whole list
   * instead: nothing was cleared, so no choice may be recorded over it.
   */
  onClearAll: (
    targets: readonly BindingTarget[],
  ) => Promise<readonly BindingTarget[]>;
}

/**
 * The reads and writes behind the `Workflow Parameters` and `For Analytics Test`
 * columns.
 *
 * Shared by the Documents tab and the Support tab's document tables rather than
 * written twice. The unmark path below holds an invariant no component can
 * restate safely, and a second copy of it is a second thing to keep correct: the
 * moment the two drift, one tab clears a document's assignments as it unflags
 * and the other strands them.
 *
 * Takes the procedure rather than fetching one, so each caller keeps whichever
 * read it already performs and this adds no third consumer of the full tree.
 */
export function useDocumentRowAnalytics(
  procedureSessionId: string,
  procedure: Procedure | undefined,
  analyticsOwner: AnalyticsOwner | undefined,
): DocumentRowAnalytics | undefined {
  const availability = useHybridTestingAvailability(analyticsOwner?.kind);
  const featureEnabled = availability !== "hidden";
  // Without an Optro sign-in the reads still run, so the bindings already
  // written stay readable; only the writes are held.
  const writesHeldReason =
    availability === "signInRequired"
      ? ANALYTICS_INPUTS_SIGN_IN_REQUIRED_COPY
      : null;
  const procedureColumns = procedure?.columns;
  const hasOwner = analyticsOwner !== undefined;
  const analyticsColumns = useMemo(() => {
    // The flag and the owner are folded in here rather than gating the
    // return, so neither a workspace without the feature nor a session without
    // an owner issues any of the reads below: with no column ids there is
    // nothing to fetch file parameters or bindings for. Every analytics route
    // is addressed by the session's control or work step, so without one there
    // is nowhere to read a file parameter from and nowhere to write a binding
    // to, and the authoring columns drawn over that offer gestures that cannot
    // complete.
    //
    // The two arms are not symmetrical, and only one of them costs anything: a
    // session with no owner has nothing to withhold, while a workspace off
    // the flag may already hold bindings whose only reading this suppresses.
    // `useHybridTestingAvailability` states that cost and the ground it is
    // taken on; this is the site where it is paid.
    const columns = featureEnabled && hasOwner ? (procedureColumns ?? []) : [];
    // `flatMap` rather than filter-then-map so the narrowing survives into the
    // body: the analytics routes resolve the *analytics column* record and take
    // `analyticsColumn.id`, and the procedure-column id 404s there.
    return columns.flatMap((column) => {
      const analyticsColumn = column.analyticsColumn;
      if (analyticsColumn == null) return [];
      return [
        {
          columnId: analyticsColumn.id,
          columnName: getColumnDisplayName(column, columns, []),
          workflowName: analyticsColumn.workflowName,
        },
      ];
    });
  }, [procedureColumns, featureEnabled, hasOwner]);

  const analyticsColumnIds = useMemo(
    () => analyticsColumns.map((column) => column.columnId),
    [analyticsColumns],
  );
  const {
    data: columnFeeds,
    isLoading: feedsLoading,
    error: feedsError,
    issued: feedsIssued,
    reread: rereadFeeds,
  } = useAnalyticsColumnFeeds(analyticsOwner, analyticsColumnIds);
  const feedsState = feedsReadState({
    answered: columnFeeds !== undefined,
    issued: feedsIssued,
    isLoading: feedsLoading,
    error: feedsError,
  });
  const { bindParameter, clearParameter } =
    useAnalyticsWorkflowActions(analyticsOwner);
  const { isPendingFor: isFlagWritePendingFor, setAnalyticsInput } =
    useAnalyticsInputFlag(procedureSessionId);

  // A counter rather than a boolean: two writes started before either settles
  // would otherwise have the first to return re-enable the picker while the
  // second is still open.
  const [bindingWrites, setBindingWrites] = useState(0);
  const bindingWritePending = bindingWrites > 0;

  // The documents whose unmark transaction is open. The flag hook knows only
  // its own round trip, and the clears that must precede it are the longer half
  // of the gesture.
  const [unmarking, setUnmarking] = useState<Set<string>>(new Set());

  const runBindingWrite = useCallback(async <T>(write: () => Promise<T>) => {
    setBindingWrites((n) => n + 1);
    try {
      return await write();
    } finally {
      setBindingWrites((n) => n - 1);
    }
  }, []);

  // One derivation for the rendered list and for the one the unmark path
  // re-reads, so the two cannot come to disagree about what a feeds answer
  // means.
  const targetsFromFeeds = useCallback(
    (feeds: AnalyticsColumnFeeds | undefined) =>
      bindingTargets(
        feedColumns({
          columns: analyticsColumns,
          feeds: feeds?.columns ?? [],
        }),
      ),
    [analyticsColumns],
  );

  const targets = useMemo(
    () => targetsFromFeeds(columnFeeds),
    [targetsFromFeeds, columnFeeds],
  );

  const deletedColumnNames = useMemo(
    () =>
      deletedWorkflowColumnNames({
        columns: analyticsColumns,
        feeds: columnFeeds?.columns ?? [],
      }),
    [analyticsColumns, columnFeeds],
  );

  const handleAssignParameter = useCallback(
    async (documentId: BoundDocumentId, target: BindingTarget) => {
      await runBindingWrite(async () => {
        try {
          // No `boundBy` is sent: the route stamps the provenance from the
          // caller, and a body that could claim `'agent'` would put a false
          // attribution in the record of how the test was configured.
          await bindParameter(documentAssignmentRequest(target, documentId));
          toast.success(`Assigned to ${target.columnName} · ${target.label}`);
        } catch (error) {
          console.error("Failed to assign the analytics input", error);
          toast.error("Failed to assign the analytics input");
        }
      });
    },
    [bindParameter, runBindingWrite],
  );

  const handleUseSampleRow = useCallback(
    async (target: BindingTarget) => {
      await runBindingWrite(async () => {
        try {
          await bindParameter(sampleRowAssignmentRequest(target));
          toast.success(
            `${target.columnName} · ${target.label} is answered by each sample's own row`,
          );
        } catch (error) {
          console.error("Failed to assign the analytics input", error);
          toast.error("Failed to assign the analytics input");
        }
      });
    },
    [bindParameter, runBindingWrite],
  );

  /**
   * Clear every assignment this document answers, stopping at the first refusal.
   *
   * Returns the pairs still assigned when it stopped, so the caller can report
   * them; empty means every one of them is cleared. Sequential rather than
   * fanned out: each clear invalidates the whole analytics prefix, and N
   * concurrent clears would have N refetches racing over the answer they are
   * each changing.
   *
   * The list to search is the caller's rather than the rendered one, because
   * the caller is the only one that knows how old the read behind it is.
   */
  const clearAssignmentsFor = useCallback(
    async (
      documentId: BoundDocumentId,
      feedTargets: readonly BindingTarget[],
    ): Promise<BindingTarget[]> => {
      const assigned = targetsAssignedTo(feedTargets, documentId);
      for (const [index, target] of assigned.entries()) {
        try {
          await clearParameter(target.columnId, target.paramKey);
        } catch (error) {
          console.error("Failed to clear the analytics input", error);
          // A route that has just refused one clear refuses the rest, so the
          // remaining pairs are reported rather than attempted.
          return assigned.slice(index);
        }
      }
      return [];
    },
    [clearParameter],
  );

  /**
   * Set or clear the flag, clearing the document's assignments as it unflags.
   *
   * Unmarking clears first and unflags second, and a failed clear stops both:
   * the flag is what renders the parameter editor, so unflagging over a
   * surviving assignment hides the only control that can reach it — the run
   * would keep consuming a file the auditor can no longer detach.
   *
   * Both controls are held pending for the whole of that, which is what makes
   * the ordering worth anything: a binding written after the re-read is behind
   * the snapshot the clears are decided on, so it survives them and is then
   * hidden by the flag landing — the stranded assignment the ordering exists to
   * rule out, arrived at from inside the path that rules it out.
   */
  const handleSetAnalyticsInput = useCallback(
    async (documentId: BoundDocumentId, next: boolean) => {
      if (next) {
        await setAnalyticsInput(documentId, true);
        return;
      }
      // Nothing is unflagged over an unread feeds list. The assignments to
      // clear are read from that list, so an empty one here means "not known"
      // rather than "none" — unflagging on it would strand the very binding the
      // clear exists to remove, and strand it invisibly, because the surface
      // that would show it is the one that has not loaded. A list known to be
      // behind is not that case: it holds an answer, and what the clear is
      // decided on is the re-read below rather than this list, so a failed
      // background refetch stops the unmark only if the re-read fails too.
      // Enumerated as the readings that pass rather than the ones that do not,
      // so a reading nobody has ruled on refuses instead of falling through.
      // `unasked` is refused on that ground and not merely by default: it holds
      // no answer at all, which is strictly less than the behind-but-real
      // answer `stale` holds, and the pairs to clear can only be named by an
      // answer.
      if (feedsState !== "ready" && feedsState !== "stale") {
        toast.error(
          "The workflow's file inputs have not loaded, so unmarking cannot clear what this file answers. Try again once they load.",
        );
        return;
      }
      setUnmarking((prev) => new Set(prev).add(documentId));
      try {
        await runBindingWrite(async () => {
          try {
            // Neither reading says how old the held answer is: no read of ours
            // fails when another client writes, so even an unfailed list can
            // already be behind. The pairs to clear are named by that list, so
            // it is re-read here and the clear decided on what came back.
            const freshFeeds = await rereadFeeds();
            if (freshFeeds === null) {
              toast.error(
                "Could not re-read the workflow's file inputs, so unmarking cannot safely clear what this file answers. Try again.",
              );
              return;
            }
            const stillAssigned = await clearAssignmentsFor(
              documentId,
              targetsFromFeeds(freshFeeds),
            );
            if (stillAssigned.length > 0) {
              const names = stillAssigned
                .map((target) => `${target.columnName} · ${target.label}`)
                .join(", ");
              toast.error(
                `Could not clear ${names}. The file is still marked as an analytics input, so the assignment can still be cleared by hand.`,
              );
              return;
            }
            // The clears are committed and cannot be taken back, so a flag write
            // that fails here leaves the gesture half-done. Said out loud because
            // the flag write's own toast reads as a refused action — an auditor
            // who took it for a no-op would believe their assignments survived it.
            if (!(await setAnalyticsInput(documentId, false))) {
              toast.error(
                "This file's parameter assignments were cleared, but it is still marked as an analytics input. Unmark it again to finish.",
              );
            }
          } catch (error) {
            // Nothing above rejects: the re-read answers null, each clear is
            // caught per pair, and the flag write answers false. Caught anyway
            // because clears commit as they go, so an unwind past here would
            // leave the one outcome every branch above exists to rule out — a
            // gesture that half-happened and said nothing. The wording claims
            // no more than that, since where it stopped is exactly what an
            // unexpected throw does not say.
            console.error("Failed to unmark the analytics input", error);
            toast.error(
              "Unmarking this file did not finish. Some of its parameter assignments may have been cleared and it is still marked as an analytics input. Check its assignments and unmark it again.",
            );
          }
        });
      } finally {
        setUnmarking((prev) => {
          const remaining = new Set(prev);
          remaining.delete(documentId);
          return remaining;
        });
      }
    },
    [
      feedsState,
      runBindingWrite,
      rereadFeeds,
      targetsFromFeeds,
      clearAssignmentsFor,
      setAnalyticsInput,
    ],
  );

  // The switch answers to both: its own flag write, which the flag hook times,
  // and the unmark transaction around it, which nothing else can see.
  const isPendingFor = useCallback(
    (documentId: string) =>
      isFlagWritePendingFor(documentId) || unmarking.has(documentId),
    [isFlagWritePendingFor, unmarking],
  );

  const handleClearParameter = useCallback(
    async (target: BindingTarget) => {
      await runBindingWrite(async () => {
        try {
          // Keyed on the pair, so clearing one assignment cannot touch the same
          // parameter key on another column.
          await clearParameter(target.columnId, target.paramKey);
          toast.success(`Cleared ${target.columnName} · ${target.label}`);
        } catch (error) {
          console.error("Failed to clear the analytics input", error);
          toast.error("Failed to clear the analytics input");
        }
      });
    },
    [clearParameter, runBindingWrite],
  );

  // Sequential for the reason `clearAssignmentsFor` is: each clear invalidates
  // the whole analytics prefix, and concurrent clears race their refetches over
  // the answer they are each changing.
  const handleClearAll = useCallback(
    async (
      toClear: readonly BindingTarget[],
    ): Promise<readonly BindingTarget[]> => {
      const pairName = (target: BindingTarget) =>
        `${target.columnName} · ${target.label}`;
      // Enumerated as the readings that pass, so a reading nobody has ruled on
      // refuses instead of falling through. Which pairs are still this row's is
      // the whole of what decides the clear, and only a held answer can be
      // re-read into a newer one: `unasked` holds none at all, so there is
      // nothing here to check the caller's list against.
      if (feedsState !== "ready" && feedsState !== "stale") {
        toast.error(
          "The workflow's file inputs have not loaded, so these inputs cannot be cleared safely. Try again once they load.",
        );
        return toClear;
      }
      // No pair named, so nothing to re-read for and nothing to report: a caller
      // that arrived here with an empty list is the one that knows why its own
      // list is empty, and it says so itself.
      if (toClear.length === 0) {
        return [];
      }
      return runBindingWrite(async () => {
        try {
          // The caller's list names pairs it read before the auditor confirmed,
          // and no read of ours fails when another client writes — an agent
          // rebinding a pair to a file in that window leaves the list still
          // claiming the pair for this row. The clear is keyed on the pair and
          // not on what fills it, so it is decided on what comes back here.
          const freshFeeds = await rereadFeeds();
          if (freshFeeds === null) {
            toast.error(
              "Could not re-read the workflow's file inputs, so these inputs were not cleared. Try again.",
            );
            return toClear;
          }
          const stillThisRow = new Set(
            targetsFilledBy(targetsFromFeeds(freshFeeds), {
              kind: "sample_row",
            }).map(targetKey),
          );
          const clearable = toClear.filter((target) =>
            stillThisRow.has(targetKey(target)),
          );
          if (clearable.length === 0) {
            // A destructive gesture the auditor confirmed and that removed
            // nothing, which silence would read as having gone through.
            toast.info(
              "Those inputs are no longer filled by each sample's own row.",
            );
            return [];
          }
          for (const [index, target] of clearable.entries()) {
            try {
              await clearParameter(target.columnId, target.paramKey);
            } catch (error) {
              console.error("Failed to clear the analytics input", error);
              // The refused pair and the ones behind it: a route that has just
              // refused one clear refuses the rest, so they are reported rather
              // than attempted.
              const remaining = clearable.slice(index);
              toast.error(
                `Could not clear ${remaining.map(pairName).join(", ")}`,
              );
              return remaining;
            }
          }
          toast.success(`Cleared ${clearable.map(pairName).join(", ")}`);
          return [];
        } catch (error) {
          // Nothing above rejects: the re-read answers null and each clear is
          // caught per pair. Caught anyway because the clears commit as they go,
          // so an unwind past here would hand the caller no answer about a
          // gesture that half-happened. Every pair is reported as possibly still
          // this row's, which is all an unexpected throw leaves known.
          console.error("Failed to clear the analytics inputs", error);
          toast.error(
            "Clearing these inputs did not finish. Check which of them are still filled.",
          );
          return toClear;
        }
      });
    },
    [
      clearParameter,
      feedsState,
      rereadFeeds,
      runBindingWrite,
      targetsFromFeeds,
    ],
  );

  return useMemo(
    () =>
      analyticsColumns.length === 0
        ? undefined
        : {
            targets,
            feedsState,
            deletedColumnNames,
            pending: bindingWritePending,
            writesHeldReason,
            isPendingFor,
            // Each write refuses itself while held, so a control a caller
            // forgot to disable still cannot write.
            onSetAnalyticsInput: (
              documentId: BoundDocumentId,
              next: boolean,
            ) => {
              if (writesHeldReason !== null) return;
              void handleSetAnalyticsInput(documentId, next);
            },
            onAssign: (documentId: BoundDocumentId, target: BindingTarget) => {
              if (writesHeldReason !== null) return;
              void handleAssignParameter(documentId, target);
            },
            onUseSampleRow: (target: BindingTarget) => {
              if (writesHeldReason !== null) return;
              void handleUseSampleRow(target);
            },
            onClear: (target: BindingTarget) => {
              if (writesHeldReason !== null) return;
              void handleClearParameter(target);
            },
            onClearAll: (toClear: readonly BindingTarget[]) =>
              writesHeldReason !== null
                ? Promise.resolve(toClear)
                : handleClearAll(toClear),
          },
    [
      analyticsColumns.length,
      writesHeldReason,
      targets,
      feedsState,
      deletedColumnNames,
      bindingWritePending,
      isPendingFor,
      handleSetAnalyticsInput,
      handleAssignParameter,
      handleUseSampleRow,
      handleClearParameter,
      handleClearAll,
    ],
  );
}
